import React, { useEffect, useState, useMemo } from 'react';
import DataTable, { FILTER_TYPES } from '../General/DataTable';
import API_STATES from '@/constants/StateConstants';
import { ByzzerMask } from '../General/ByzzerMask/ByzzerMask';
import generalStyles from '@/components/General/Styles.module.scss';
import { getThirdPartyAgreementsReport } from '@/services/api/report.service';
import { handleApiError } from '@/components/General/HandleApiError';
import { formatTpaDate } from '../CompanyConfig/TpaTable/TpaTable';
import { DateRangeColumnFilter } from '../General/DateRangeColumnFilter';

const TPA_TYPE_LABELS = {
    niq_partner: 'NIQ Partner',
    broker: 'Broker',
    consultant: 'Consultant',
    other: 'Other',
};

const ThirdPartyAgreementsReport = () => {
    const [reportState, setReportState] = useState({
        status: API_STATES.none,
        error: '',
        data: [],
    });

    const tpaReportColumns = useMemo(
        () => [
            {
                Header: 'TPA Client Company',
                accessor: 'tpaClientCompany',
                className: generalStyles.tableCell,
            },
            {
                Header: 'TPA Provider Company',
                accessor: 'tpaProviderCompany',
                className: generalStyles.tableCell,
            },
            {
                Header: 'TPA Type',
                accessor: 'tpaType',
                className: generalStyles.tableCell,
                Cell: ({ value }) => TPA_TYPE_LABELS[value] || value,
            },
            {
                Header: 'TPA Link',
                accessor: 'tpaLink',
                className: generalStyles.tableCell,
                Cell: ({ value }) => (
                    <a href={value} target="_blank" rel="noopener noreferrer">
                        {value}
                    </a>
                ),
            },
            {
                Header: 'TPA Start Date',
                accessor: 'tpaStartDate',
                className: generalStyles.tableCell,
                filter: FILTER_TYPES.dateRange,
                Filter: DateRangeColumnFilter,
                Cell: ({ value }) => formatTpaDate(value),
            },
            {
                Header: 'TPA End Date',
                accessor: 'tpaEndDate',
                className: generalStyles.tableCell,
                filter: FILTER_TYPES.dateRange,
                Filter: DateRangeColumnFilter,
                Cell: ({ value }) => formatTpaDate(value),
            },
            {
                Header: 'Created Date',
                accessor: 'createdDate',
                className: generalStyles.tableCell,
            },
            {
                Header: 'Created By',
                accessor: 'createdBy',
                className: generalStyles.tableCell,
            },
        ],
        []
    );

    const loadData = async () => {
        setReportState({ status: API_STATES.loading, error: '', data: [] });
        const result = await getThirdPartyAgreementsReport();
        switch (result.status) {
            case API_STATES.success:
                setReportState({ status: API_STATES.success, error: '', data: result.data });
                break;
            case API_STATES.error:
                handleApiError(
                    result.error,
                    (status) => setReportState((prev) => ({ ...prev, status })),
                    (error) => setReportState((prev) => ({ ...prev, error })),
                    'Failed to load third party agreements report'
                );
                break;
            default:
                setReportState({ status: API_STATES.none, error: '', data: [] });
        }
    };

    useEffect(() => {
        loadData();
    }, []);

    return (
        <>
            <h1>Third Party Agreements Report</h1>
            <hr />
            <div data-test="tpa-report-container">
                {reportState.status === API_STATES.loading && <ByzzerMask loading={true} />}
                {reportState.status === API_STATES.error && (
                    <p className={generalStyles.errorMessage}>Error: {reportState.error}</p>
                )}
                <DataTable
                    data={reportState.data}
                    columns={tpaReportColumns}
                    downloadFileName={'ThirdPartyAgreementsReport'}
                    withDownload
                    noDataDescription="No third party agreements found"
                />
            </div>
        </>
    );
};

export default ThirdPartyAgreementsReport;
